<template>
  <div class="max-w-5xl mx-auto">
    <Head title="Crear cliente" />

    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-2">
        <button class="hover:text-orange-500 transition-colors" @click="back">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/clients" class="hover:text-orange-500 transition-colors">Clientes</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">Crear</span>
      </div>
      
      <h1 class="text-2xl font-bold text-gray-900">Registrar Nuevo Cliente</h1>
      <p class="text-gray-500 mt-1">Complete los detalles para añadir un nuevo cliente a su sistema.</p>
    </div>

    <!-- Form Layout -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <form @submit.prevent="store">
        <div class="p-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <!-- Empresa -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Empresa</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-building text-gray-400" />
                </div>
                <input 
                  v-model="form.companyName"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.companyName}"
                  placeholder="Ingrese el nombre de la empresa..."
                />
              </div>
              <p v-if="form.errors.companyName" class="text-red-500 text-xs mt-1">{{ form.errors.companyName }}</p>
            </div>

            <!-- Nombre -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Nombre y Apellido</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-user text-gray-400" />
                </div>
                <input 
                  v-model="form.name"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.name}"
                  placeholder="Ingrese el nombre completo..."
                />
              </div>
              <p v-if="form.errors.name" class="text-red-500 text-xs mt-1">{{ form.errors.name }}</p>
            </div>

            <!-- Email -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Correo electrónico</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-envelope text-gray-400" />
                </div>
                <input 
                  v-model="form.email"
                  type="email" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.email}"
                  placeholder="ejemplo@correo.com"
                />
              </div>
              <p v-if="form.errors.email" class="text-red-500 text-xs mt-1">{{ form.errors.email }}</p>
            </div>

            <!-- Identificación -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Alias o Cédula</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-id-card text-gray-400" />
                </div>
                <input 
                  v-model="form.identification"
                  type="text" 
                  maxlength="15"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.identification || customErrors.identification}"
                  placeholder="Ingrese identificación..."
                  @keypress="onlyNumbers($event, 'identification')"
                  @input="onNumericInput('identification', $event)"
                />
              </div>
              <p v-if="form.errors.identification" class="text-red-500 text-xs mt-1">{{ form.errors.identification }}</p>
              <p v-if="customErrors.identification" class="text-red-500 text-xs mt-1">{{ customErrors.identification }}</p>
            </div>

            <!-- Días de Crédito -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Días de Crédito</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-calendar-alt text-gray-400" />
                </div>
                <input 
                  v-model="form.credit_days"
                  type="number" 
                  min="0"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.credit_days}"
                  placeholder="Ej: 30"
                />
              </div>
              <p v-if="form.errors.credit_days" class="text-red-500 text-xs mt-1">{{ form.errors.credit_days }}</p>
            </div>

            <!-- Teléfono -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Teléfono</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-phone text-gray-400" />
                </div>
                <input 
                  v-model="form.phone"
                  type="tel" 
                  maxlength="15"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.phone || customErrors.phone}"
                  placeholder="Ingrese número telefónico..."
                  @keypress="onlyNumbers($event, 'phone')"
                  @input="onNumericInput('phone', $event)"
                />
              </div>
              <p v-if="form.errors.phone" class="text-red-500 text-xs mt-1">{{ form.errors.phone }}</p>
              <p v-if="customErrors.phone" class="text-red-500 text-xs mt-1">{{ customErrors.phone }}</p>
            </div>

            <!-- Dirección -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Dirección</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-map-marker-alt text-gray-400" />
                </div>
                <input 
                  v-model="form.address"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.address}"
                  placeholder="Ingrese dirección completa..."
                />
              </div>
              <p v-if="form.errors.address" class="text-red-500 text-xs mt-1">{{ form.errors.address }}</p>
            </div>

            <!-- Tipo de Cliente -->
            <div class="space-y-1.5 md:col-span-2 lg:col-span-1">
              <label class="block text-sm font-medium text-gray-700">Tipo de cliente</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-shapes text-gray-400" />
                </div>
                <select 
                  v-model="form.typeClient" 
                  class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-gray-700 text-sm appearance-none"
                  :class="{'border-red-300': form.errors.typeClient}"
                >
                  <option value="" disabled>Seleccionar tipo...</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="spot">SPOT</option>
                  <option value="a">A</option>
                  <option value="b">B</option>
                  <option value="c">C</option>
                </select>
                <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <i class="fa fa-chevron-down text-gray-400 text-xs" />
                </div>
              </div>
              <p v-if="form.errors.typeClient" class="text-red-500 text-xs mt-1">{{ form.errors.typeClient }}</p>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p class="text-xs text-gray-500 order-2 sm:order-1">
            * Todos los campos son obligatorios para el registro.
          </p>
          
          <div class="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
            <button 
              type="button" 
              class="px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 font-medium rounded-lg transition-colors text-sm w-full sm:w-auto"
              @click="back"
            >
              Cancelar
            </button>
            <loading-button 
              :loading="form.processing" 
              class="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors text-sm w-full sm:w-auto flex justify-center items-center gap-2 shadow-sm"
              type="submit"
            >
              <i class="fa fa-plus-circle" />
              Crear cliente
            </loading-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  name: 'ClientsCreate',

  components: {
    Head,
    Link,
    LoadingButton,
  },

  layout: Layout,

  remember: 'form',

  data() {
    return {
      customErrors: {
        identification: '',
        phone: '',
      },
      form: this.$inertia.form({
        companyName: '',
        name: '',
        email: '',
        identification: '',
        phone: '',
        address: '',
        typeClient: '',
        credit_days: 0,
      }),
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    onlyNumbers(event, field) {
      const charCode = event.which ? event.which : event.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
        this.customErrors[field] = 'Solo se permiten números en este campo'
        setTimeout(() => {
          if (this.customErrors[field] === 'Solo se permiten números en este campo') {
            this.customErrors[field] = ''
          }
        }, 3000)
      }
    },

    onNumericInput(field, event) {
      let val = event.target.value
      const max = 15
      if (/[^\d]/.test(val)) {
        this.customErrors[field] = 'Solo se permiten números en este campo'
        val = val.replace(/\D/g, '')
      } else {
        this.customErrors[field] = ''
      }
      if (val.length > max) {
        val = val.slice(0, max)
        this.customErrors[field] = `Máximo ${max} dígitos permitidos`
      }
      this.form[field] = val
    },

    store() {
      let hasError = false
      if (!this.form.identification || /[^\d]/.test(this.form.identification)) {
        this.customErrors.identification = 'Solo se permiten números en este campo'
        hasError = true
      }
      if (this.form.phone && /[^\d]/.test(this.form.phone)) {
        this.customErrors.phone = 'Solo se permiten números en este campo'
        hasError = true
      }
      if (hasError) return

      this.form.post('/clients')
    },
  },
}
</script>
